import { useCallback, useEffect, useState } from 'react'
import { useCIMSModal } from 'components/CIMSModal/hooks/useCIMSModal'
import {
  EditFilenameTemplateItemInput,
  useEditFilenameModalMetadataQuery,
} from 'schema'
import { FilenameTemplate } from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { EditFilenameModalState, UseEditFilenameModal } from './types'

export function useEditFilenameModal<
  T extends EditFilenameModalState
>(): UseEditFilenameModal<T> {
  const [visible, toggle, state] = useCIMSModal<T>()
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const [templateItems, setTemplateItems] = useState<
    EditFilenameTemplateItemInput[]
  >([])
  const { data } = useEditFilenameModalMetadataQuery({
    ...DefaultQueryHookOptions,
    skip: !projectGlobalId,
    variables: { projectGlobalId: projectGlobalId! },
  })
  const [valid, setValid] = useState(true)
  useEffect(() => {
    if (!state?.templateItems)
      return setTemplateItems(
        (state?.defaultTemplateItems ?? []).map((item) => {
          let value
          if (item.type === 'custom') value = item.text
          else value = item.templateItemId
          return { type: item.type, value }
        })
      )
    return setTemplateItems(
      (state?.templateItems ?? []).map(({ __typename, ...item }) => item)
    )
  }, [state?.defaultTemplateItems, state?.templateItems])
  useEffect(() => {
    setValid(
      templateItems.every(({ type, value }) => {
        if (type !== 'custom') return true
        return !FilenameTemplate.isIllegal(value)
      })
    )
  }, [templateItems])
  const isDisabled = useCallback<UseEditFilenameModal<T>['isDisabled']>(
    (item) => {
      if (!item) return true
      const { property } =
        typeof item === 'string' ? FilenameTemplate.fromIdOrFail(item) : item
      if (property === 'projectName' && !data?.project?.projectName) return true
      else if (
        property === 'clientProjectName' &&
        !data?.project?.clientProjectName
      )
        return true
      else if (
        property === 'clientProjectNumber' &&
        !data?.project?.clientProjectNumber
      )
        return true
      return false
    },
    [data?.project]
  )
  return {
    visible,
    toggle,
    state,
    templateItems,
    setTemplateItems,
    valid,
    isDisabled,
  }
}
